import { useState, useEffect, useRef, useCallback } from "react";
import { useSEO } from "@/hooks/use-seo";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, XCircle, Loader2, Eye, Share2, BarChart3, Shield, Globe, Lock, Mail, Phone, Megaphone, ExternalLink, Gem, Sparkles, Home, Leaf, Package, TrendingUp, Rocket, Zap, Trophy, BookOpen, Users, Send, Music, Pin, Ban, Coins, Wallet, PiggyBank, Diamond, Landmark, Target, ScanEye, RefreshCcw, ChartPie, Settings, Sprout, Search, type LucideIcon } from "lucide-react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import logoWrkt from "@assets/Wrkt_-_PNG_1771840403931.png";
import signalReport from "@assets/signal-report.webp";
import { apiRequest } from "@/lib/queryClient";
import { gaEvent, fbqTrack } from "@/lib/analytics";

interface ScanResults {
  url: string;
  domain: string;
  seo: { title: string | null; description: string | null; hasOgTags: boolean; hasTwitterCards: boolean; score: number };
  socials: { facebook: string | null; instagram: string | null; tiktok: string | null; linkedin: string | null; youtube: string | null; pinterest: string | null; score: number };
  tracking: { metaPixel: boolean; googleAnalytics: boolean; tiktokPixel: boolean; googleTagManager: boolean; emailMarketing?: boolean; emailPlatform?: string | null; score: number };
  technical: { hasHttps: boolean; hasViewport: boolean; hasFavicon: boolean; score: number };
  performance?: { ttfbMs: number; htmlKB: number; syncHeadScripts: number; hasLazyLoading: boolean; hasModernImages: boolean; crux: { lcpMs: number | null; inpMs: number | null; cls: number | null } | null; score: number };
  crm?: { emailPlatform: string | null; hasSignupForm: boolean; hasOnsiteForms: boolean; reviewsPlatform: string | null; loyaltyPlatform: string | null; smsPlatform: string | null; score: number };
  advertising: { hasConversionEvents: boolean; conversionEvents: string[]; hasMetaAds: boolean; metaPixelId: string | null; hasGoogleAds: boolean; googleAdsId: string | null; googleConversionEvents: string[]; hasServerSideTracking: boolean; hasDynamicRemarketing: boolean; facebookPageId: string | null; adLibraryUrl: string | null; score: number };
  overallScore: number;
}

function countImprovements(r: ScanResults): number {
  let n = 0;
  if (!r.seo.title) n++; if (!r.seo.description) n++; if (!r.seo.hasOgTags) n++; if (!r.seo.hasTwitterCards) n++;
  [r.socials.facebook, r.socials.instagram, r.socials.tiktok, r.socials.linkedin, r.socials.youtube, r.socials.pinterest].forEach(v => { if (!v) n++; });
  if (!r.tracking.metaPixel) n++; if (!r.tracking.googleAnalytics) n++; if (!r.tracking.tiktokPixel) n++; if (!r.tracking.googleTagManager) n++; if (!r.tracking.emailMarketing) n++;
  if (!r.technical.hasHttps) n++; if (!r.technical.hasViewport) n++; if (!r.technical.hasFavicon) n++;
  if (!r.advertising.hasMetaAds) n++; if (!r.advertising.hasGoogleAds) n++; if (!r.advertising.hasConversionEvents) n++; if (!r.advertising.hasServerSideTracking) n++; if (!r.advertising.hasDynamicRemarketing) n++;
  if (r.performance) { if (r.performance.ttfbMs >= 800) n++; if (r.performance.htmlKB >= 400) n++; if (r.performance.syncHeadScripts > 3) n++; if (!r.performance.hasLazyLoading) n++; if (!r.performance.hasModernImages) n++; }
  if (r.crm) { if (!r.crm.emailPlatform) n++; if (!r.crm.hasSignupForm) n++; if (!r.crm.reviewsPlatform) n++; }
  return n;
}

function countChecks(r: ScanResults): number {
  let n = 23; // vaste checks: seo 4, socials 6, tracking 5, technisch 3, advertising 5
  if (r.performance) n += 5;
  if (r.crm) n += 3;
  return n;
}

function useQuestions() {
  const { t } = useLanguage();
  return [
    {
      id: 'vertical', cat: t('scan_q1_cat'),
      title: t('scan_q1_title'),
      sub: t('scan_q1_sub'),
      type: 'single' as const,
      opts: [
        { i: Gem, l: t('scan_q1_o1_l'), d: t('scan_q1_o1_d'), v: 'fashion' },
        { i: Sparkles, l: t('scan_q1_o2_l'), d: t('scan_q1_o2_d'), v: 'beauty' },
        { i: Home, l: t('scan_q1_o3_l'), d: t('scan_q1_o3_d'), v: 'home' },
        { i: Leaf, l: t('scan_q1_o4_l'), d: t('scan_q1_o4_d'), v: 'food' },
        { i: Package, l: t('scan_q1_o5_l'), d: t('scan_q1_o5_d'), v: 'other' }
      ]
    },
    {
      id: 'revenue', cat: t('scan_q2_cat'),
      title: t('scan_q2_title'),
      sub: t('scan_q2_sub'),
      type: 'single' as const,
      opts: [
        { i: Sprout, l: t('scan_q2_o1_l'), d: t('scan_q2_o1_d'), v: 'u100' },
        { i: TrendingUp, l: t('scan_q2_o2_l'), d: t('scan_q2_o2_d'), v: '100_500' },
        { i: Rocket, l: t('scan_q2_o3_l'), d: t('scan_q2_o3_d'), v: '500_2m' },
        { i: Zap, l: t('scan_q2_o4_l'), d: t('scan_q2_o4_d'), v: '2_10m' },
        { i: Trophy, l: t('scan_q2_o5_l'), d: t('scan_q2_o5_d'), v: 'o10m' }
      ],
    },
    {
      id: 'channels', cat: t('scan_q3_cat'),
      title: t('scan_q3_title'),
      sub: t('scan_q3_sub'),
      type: 'multi' as const,
      opts: [
        { i: BookOpen, l: t('scan_q3_o1_l'), d: t('scan_q3_o1_d'), v: 'meta' },
        { i: Search, l: t('scan_q3_o2_l'), d: t('scan_q3_o2_d'), v: 'google' },
        { i: Music, l: t('scan_q3_o3_l'), d: t('scan_q3_o3_d'), v: 'tiktok' },
        { i: Pin, l: t('scan_q3_o4_l'), d: t('scan_q3_o4_d'), v: 'pinterest' },
        { i: Send, l: t('scan_q3_o5_l'), d: t('scan_q3_o5_d'), v: 'email' },
        { i: Users, l: t('scan_q3_o6_l'), d: t('scan_q3_o6_d'), v: 'influencer' },
        { i: Ban, l: t('scan_q3_o7_l'), d: t('scan_q3_o7_d'), v: 'none' }
      ]
    },
    {
      id: 'budget', cat: t('scan_q4_cat'),
      title: t('scan_q4_title'),
      sub: t('scan_q4_sub'),
      type: 'single' as const,
      opts: [
        { i: Coins, l: t('scan_q4_o1_l'), d: t('scan_q4_o1_d'), v: 'u1k' },
        { i: Wallet, l: t('scan_q4_o2_l'), d: t('scan_q4_o2_d'), v: '1_5k' },
        { i: PiggyBank, l: t('scan_q4_o3_l'), d: t('scan_q4_o3_d'), v: '5_20k' },
        { i: Diamond, l: t('scan_q4_o4_l'), d: t('scan_q4_o4_d'), v: '20_75k' },
        { i: Landmark, l: t('scan_q4_o5_l'), d: t('scan_q4_o5_d'), v: 'o75k' }
      ],
    },
    {
      id: 'painpoint', cat: t('scan_q5_cat'),
      title: t('scan_q5_title'),
      sub: t('scan_q5_sub'),
      type: 'multi' as const,
      opts: [
        { i: Target, l: t('scan_q5_o1_l'), d: t('scan_q5_o1_d'), v: 'roas' },
        { i: ScanEye, l: t('scan_q5_o2_l'), d: t('scan_q5_o2_d'), v: 'awareness' },
        { i: RefreshCcw, l: t('scan_q5_o3_l'), d: t('scan_q5_o3_d'), v: 'retention' },
        { i: ChartPie, l: t('scan_q5_o4_l'), d: t('scan_q5_o4_d'), v: 'data' },
        { i: Settings, l: t('scan_q5_o5_l'), d: t('scan_q5_o5_d'), v: 'structure' }
      ]
    }
  ];
}

function StatusIcon({ active }: { active: boolean }) {
  return active
    ? <CheckCircle2 className="w-4 h-4 text-[#4CEAAC] shrink-0" />
    : <XCircle className="w-4 h-4 text-red-400/70 shrink-0" />;
}

function ScoreRing({ score, size = 140 }: { score: number; size?: number }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? "#4CEAAC" : score >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }} transition={{ duration: 0.8, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span className="text-4xl font-display font-bold" style={{ color }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.3 }}>
          {score}
        </motion.span>
        <span className="text-[10px] font-bold text-white/50 uppercase">/ 100</span>
      </div>
    </div>
  );
}

// Flow steps:
// -1 = landing (URL input)
// 0 = scanning/loading animation
// 1-5 = quiz questions (QUESTIONS[0..4])
// 6 = email gate (big email capture)
// 7 = full results

const stepToPath = (step: number, prefix: string): string => {
  const en = prefix === '/en';
  switch (step) {
    case -1: return `${prefix}/scan`;
    case 0: return `${prefix}/scan/${en ? 'loading' : 'laden'}`;
    case 1: return `${prefix}/scan/${en ? 'step-1' : 'stap-1'}`;
    case 2: return `${prefix}/scan/${en ? 'step-2' : 'stap-2'}`;
    case 3: return `${prefix}/scan/${en ? 'step-3' : 'stap-3'}`;
    case 4: return `${prefix}/scan/${en ? 'step-4' : 'stap-4'}`;
    case 5: return `${prefix}/scan/${en ? 'step-5' : 'stap-5'}`;
    case 6: return `${prefix}/scan/contact`;
    case 7: return `${prefix}/scan/${en ? 'result' : 'resultaat'}`;
    default: return `${prefix}/scan`;
  }
};

const pathToStep = (pathname: string): number => {
  const path = pathname.replace(/^\/en/, '');
  if (path === '/scan/stap-1' || path === '/scan/step-1') return 1;
  if (path === '/scan/stap-2' || path === '/scan/step-2') return 2;
  if (path === '/scan/stap-3' || path === '/scan/step-3') return 3;
  if (path === '/scan/stap-4' || path === '/scan/step-4') return 4;
  if (path === '/scan/stap-5' || path === '/scan/step-5') return 5;
  if (path === '/scan/contact') return 6;
  if (path === '/scan/resultaat' || path === '/scan/result') return 7;
  if (path === '/scan/laden' || path === '/scan/loading') return 0;
  return -1;
};

export default function Scan() {
  const [location, setLocation] = useLocation();
  const { t, language, localePath } = useLanguage();
  const nl = language === 'nl';
  const prefix = language === 'en' ? '/en' : '';
  const QUESTIONS = useQuestions();
  const initialStep = pathToStep(location);
  const [step, setStep] = useState(initialStep <= 0 ? -1 : initialStep);

  useSEO({
    title: nl
      ? 'Gratis Growth Scan | Wrkt Digital - Website Analyse Tool voor E-commerce'
      : 'Free Growth Scan | Wrkt Digital - Website Analysis Tool for E-commerce',
    description: nl
      ? 'Gratis Growth Scan van Wrkt Digital: analyseer je website op SEO, social media kanalen, Meta Pixel tracking, conversion events, remarketing setup en advertenties. Direct inzicht in je groei-score met concrete quick wins voor D2C e-commerce.'
      : 'Free Growth Scan by Wrkt Digital: analyze your website for SEO, social media channels, Meta Pixel tracking, conversion events, remarketing setup and advertising. Instant growth score with actionable quick wins for D2C e-commerce.',
    path: '/scan',
    lang: nl ? 'nl' : 'en',
    keywords: nl
      ? 'gratis website analyse, growth scan, website scan tool, SEO analyse, tracking audit, Meta Pixel check, conversion tracking analyse, e-commerce website audit, digital marketing scan, remarketing check, social media analyse, gratis marketing audit'
      : 'free website analysis, growth scan, website scan tool, SEO analysis, tracking audit, Meta Pixel check, conversion tracking analysis, e-commerce website audit, digital marketing scan, remarketing check, social media analysis, free marketing audit',
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const targetPath = stepToPath(step, prefix);
    const currentPath = window.location.pathname;
    if (currentPath !== targetPath) {
      if (step === 0) {
        window.history.replaceState(null, '', targetPath);
      } else {
        window.history.pushState(null, '', targetPath);
      }
    }
  }, [step, prefix]);

  const [contactUrl, setContactUrl] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingDetail, setLoadingDetail] = useState("");
  const [scanResults, setScanResults] = useState<ScanResults | null>(null);
  const [scanId, setScanId] = useState<string | null>(null);
  const [metaAds, setMetaAds] = useState<{ status: string; adsCount?: number; capped?: boolean; sampleAds?: { thumbnailUrl: string; videoUrl?: string; bodyText: string; startDate: string; platforms: string[]; ctaText: string }[]; aiInsight?: string } | null>(null);

  // Meta Ad Library-check: pollen tot resultaat (max ~3 min)
  useEffect(() => {
    if (!scanId || !metaAds || metaAds.status !== 'pending') return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const res = await fetch(`/api/quick-scan/${scanId}/meta-ads?lang=${language}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.status === 'pending' && tries < 30) {
          setTimeout(tick, 6000);
        } else {
          setMetaAds(data.status === 'pending' ? { status: 'error' } : data);
        }
      } catch {
        if (!cancelled && tries < 30) setTimeout(tick, 6000);
      }
    };
    const t = setTimeout(tick, 6000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [scanId, metaAds?.status]);
  const [scanFailed, setScanFailed] = useState(false);
  const scanFailedRef = useRef(false);
  const scanFetchedRef = useRef(false);

  useEffect(() => {
    const onPopState = () => {
      const newStep = pathToStep(window.location.pathname);
      if (newStep === -1 || (newStep >= 1 && !scanResults && !scanFailed)) {
        setStep(-1);
        window.history.replaceState(null, '', stepToPath(-1, prefix));
      } else {
        setStep(newStep);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [prefix, scanResults, scanFailed]);

  const handleOptionSelect = (qId: string, value: string, isMulti: boolean) => {
    if (isMulti) {
      setAnswers(prev => {
        const current = prev[qId] || [];
        return current.includes(value)
          ? { ...prev, [qId]: current.filter((v: string) => v !== value) }
          : { ...prev, [qId]: [...current, value] };
      });
    } else {
      setAnswers(prev => ({ ...prev, [qId]: value }));
      const qIndex = step - 1;
      if (qIndex < QUESTIONS.length - 1) {
        setTimeout(() => setStep(s => s + 1), 400);
      }
    }
  };

  const isSelected = (qId: string, value: string) => {
    const a = answers[qId];
    if (!a) return false;
    return Array.isArray(a) ? a.includes(value) : a === value;
  };

  const canGoNextQuiz = () => {
    const qIndex = step - 1;
    const q = QUESTIONS[qIndex];
    if (!q) return false;
    if (q.type === 'multi') return (answers[q.id] || []).length > 0;
    return !!answers[q.id];
  };

  const calculateScore = () => {
    let s = 0;
    const rm: Record<string, number> = { u100: 12, '100_500': 30, '500_2m': 55, '2_10m': 75, o10m: 88 };
    s += (rm[answers.revenue] || 0);
    const ch = answers.channels || [];
    if (!ch.includes('none')) {
      s += ch.length * 6;
      if (!ch.includes('email')) s -= 8;
      if (!ch.includes('meta')) s -= 5;
      if (!ch.includes('google')) s -= 5;
    }
    const bm: Record<string, number> = { u1k: 4, '1_5k': 16, '5_20k': 36, '20_75k': 58, o75k: 74 };
    s += (bm[answers.budget] || 0) * 0.28;
    if (scanResults) {
      s += Math.round(scanResults.overallScore * 0.15);
    }
    return Math.min(97, Math.max(20, Math.round(s)));
  };

  const scanTaskSteps = nl ? [
    { label: 'Website verbinding', detail: 'Verbinding maken met server...' },
    { label: 'Social Media Detectie', detail: 'Facebook, Instagram, TikTok, Pinterest zoeken...' },
    { label: 'GTM Container Analyse', detail: 'Google Tag Manager container ophalen...' },
    { label: 'Meta Pixel Scan', detail: 'Facebook/Meta Pixel & Pixel ID detecteren...' },
    { label: 'Server-Side Tracking', detail: 'SST container & CAPI controleren...' },
    { label: 'Conversion Events', detail: 'Purchase, AddToCart & Lead events detecteren...' },
    { label: 'Remarketing Setup', detail: 'Dynamic remarketing & retargeting checken...' },
    { label: 'Ad Library Check', detail: 'Meta Ad Library & actieve ads zoeken...' },
    { label: 'Technische Check', detail: 'HTTPS, viewport & performance reviewen...' },
    { label: 'Rapport Generatie', detail: 'Groei Score & rapport samenstellen...' },
  ] : [
    { label: 'Website Connection', detail: 'Connecting to server...' },
    { label: 'Social Media Detection', detail: 'Finding Facebook, Instagram, TikTok, Pinterest...' },
    { label: 'GTM Container Analysis', detail: 'Fetching Google Tag Manager container...' },
    { label: 'Meta Pixel Scan', detail: 'Detecting Facebook/Meta Pixel & Pixel ID...' },
    { label: 'Server-Side Tracking', detail: 'Checking SST container & CAPI...' },
    { label: 'Conversion Events', detail: 'Detecting Purchase, AddToCart & Lead events...' },
    { label: 'Remarketing Setup', detail: 'Checking dynamic remarketing & retargeting...' },
    { label: 'Ad Library Check', detail: 'Searching Meta Ad Library & active ads...' },
    { label: 'Technical Check', detail: 'Reviewing HTTPS, viewport & performance...' },
    { label: 'Report Generation', detail: 'Compiling Growth Score & report...' },
  ];

  const [urlError, setUrlError] = useState('');

  const calculateLiveScore = useCallback(() => {
    let s = 0;
    let answered = 0;
    const rm: Record<string, number> = { u100: 12, '100_500': 30, '500_2m': 55, '2_10m': 75, o10m: 88 };
    if (answers.revenue) { s += (rm[answers.revenue] || 0); answered++; }
    const ch = answers.channels || [];
    if (ch.length > 0) {
      if (!ch.includes('none')) { s += ch.length * 6; if (!ch.includes('email')) s -= 8; if (!ch.includes('meta')) s -= 5; if (!ch.includes('google')) s -= 5; }
      answered++;
    }
    const bm: Record<string, number> = { u1k: 4, '1_5k': 16, '5_20k': 36, '20_75k': 58, o75k: 74 };
    if (answers.budget) { s += (bm[answers.budget] || 0) * 0.28; answered++; }
    if (answers.vertical) answered++;
    const pp = answers.painpoint;
    if (Array.isArray(pp) ? pp.length > 0 : !!pp) answered++;
    if (scanResults) s += Math.round(scanResults.overallScore * 0.15);
    const projected = answered > 0 ? Math.min(97, Math.max(20, Math.round(s * (5 / Math.max(answered, 1)) * 0.35))) : 0;
    return { score: projected, answered };
  }, [answers, scanResults]);

  const startScan = () => {
    let url = contactUrl.trim();
    if (url.length < 4) return;

    if (!url.match(/^https?:\/\//i)) url = 'https://' + url;
    const domainPattern = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/i;
    if (!domainPattern.test(url)) {
      setUrlError(nl ? 'Vul een geldige URL in, bijv. jouwwebsite.nl' : 'Enter a valid URL, e.g. yourwebsite.com');
      return;
    }
    setUrlError('');
    setContactUrl(url);
    fbqTrack('InitiateCheckout', { content_name: 'Growth Scan Scan', content_category: 'Scan' });
    gaEvent('scan_start', { language });
    gaEvent('scan_loading', { language });
    setStep(0);
    setScanFailed(false);
    scanFailedRef.current = false;
    setScanResults(null);
    setScanId(null);
    scanFetchedRef.current = false;

    apiRequest("POST", "/api/quick-scan", { url })
      .then(res => res.json())
      .then(data => {
        if (data.results) {
          setScanResults(data.results);
          setScanId(data.id);
          if (data.metaAdsPending) setMetaAds({ status: 'pending' });
        } else {
          setScanFailed(true);
          scanFailedRef.current = true;
        }
        scanFetchedRef.current = true;
      })
      .catch(err => {
        console.error("Scan API error:", err);
        setScanFailed(true);
        scanFailedRef.current = true;
        scanFetchedRef.current = true;
      });

    let currentStep = 0;
    setLoadingStep(0);
    setLoadingDetail(scanTaskSteps[0].detail);

    const delays = [600, 800, 1000, 900, 800, 700, 600, 1100, 1000, 900, 700, 800];
    const runNext = () => {
      currentStep++;
      if (currentStep < scanTaskSteps.length) {
        setLoadingStep(currentStep);
        setLoadingDetail(scanTaskSteps[currentStep].detail);
        setTimeout(runNext, delays[currentStep] || 800);
      } else {
        setLoadingStep(scanTaskSteps.length);
        const checkAndProceed = () => {
          if (scanFetchedRef.current) {
            setLoadingDetail(nl ? 'Scan compleet!' : 'Scan complete!');
            setTimeout(() => {
              if (scanFailedRef.current) return;
              setStep(1);
            }, 600);
          } else {
            setTimeout(checkAndProceed, 200);
          }
        };
        checkAndProceed();
      }
    };
    setTimeout(runNext, delays[0]);
  };

  const nextQuizStep = () => {
    if (!canGoNextQuiz()) return;
    const qIndex = step - 1;
    if (qIndex >= QUESTIONS.length - 1) {
      setStep(6);
    } else {
      setStep(s => s + 1);
    }
  };

  const prevQuizStep = () => {
    if (step > 1) {
      setStep(s => s - 1);
    }
  };

  const getHubSpotCookie = () => {
    const name = "hubspotutk=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1);
      if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
    }
    return undefined;
  };

  const formatAnswers = () => {
    const lines: string[] = [];
    const q1 = QUESTIONS[0];
    const opt1 = q1.opts.find(o => o.v === answers.vertical);
    if (opt1) lines.push(`${t('scan_hub_cat')}: ${opt1.l}`);
    const q2 = QUESTIONS[1];
    const opt2 = q2.opts.find(o => o.v === answers.revenue);
    if (opt2) lines.push(`${t('scan_hub_revenue')}: ${opt2.l}`);
    const q3 = QUESTIONS[2];
    const ch = answers.channels || [];
    const chLabels = ch.map((v: string) => q3.opts.find((o: any) => o.v === v)?.l).filter(Boolean);
    if (chLabels.length > 0) lines.push(`${t('scan_hub_channels')}: ${chLabels.join(', ')}`);
    const q4 = QUESTIONS[3];
    const opt4 = q4.opts.find(o => o.v === answers.budget);
    if (opt4) lines.push(`${t('scan_hub_budget')}: ${opt4.l}`);
    const q5 = QUESTIONS[4];
    const opt5 = q5.opts.find(o => o.v === answers.painpoint);
    if (opt5) lines.push(`${t('scan_hub_painpoint')}: ${opt5.l}`);
    return lines.join('\n');
  };

  const submitEmailAndUnlock = () => {
    if (!contactEmail.includes('@')) return;
    const hutk = getHubSpotCookie();
    const score = calculateScore();
    const scanStatus = scanResults
      ? `Website Scan Score: ${scanResults.overallScore} / 100`
      : 'Website Scan: Could not complete';
    const scanAnswersWithScore = formatAnswers() + `\n\n${t('scan_hub_score')}: ${score} / 100\n${scanStatus}\nURL: ${contactUrl}`;

    try {
      fetch('https://api.hsforms.com/submissions/v3/integration/submit/612516/04b1a42f-8aa1-4642-8b53-5c49d6919443', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'email', value: contactEmail },
            { name: 'company', value: contactUrl },
            ...(contactPhone ? [{ name: 'phone', value: contactPhone }] : []),
            ...(answers.budget ? [{ name: 'monthly_ad_budget2', value: (() => { const labels: Record<string, string> = { u1k: '< €1.000', '1_5k': '€1.000 - €5.000', '5_20k': '€5.000 - €20.000', '20_75k': '€20.000 - €75.000', o75k: '> €75.000' }; return labels[answers.budget] || answers.budget; })() }] : []),
            ...(answers.revenue ? [{ name: 'annualrevenue', value: (() => { const labels: Record<string, string> = { u100: 'Onder €100K', '100_500': '€100K - €500K', '500_2m': '€500K - €2M', '2_10m': '€2M - €10M', o10m: 'Boven €10M' }; return labels[answers.revenue] || answers.revenue; })() }] : []),
            ...(answers.revenue || answers.budget ? [{ name: 'lead_qualification', value: (() => {
              const isHighRevenue = answers.revenue && answers.revenue !== 'u100';
              const isLowBudget = !answers.budget || answers.budget === 'u1k';
              const isLowRevenue = answers.revenue === 'u100';
              if (isHighRevenue) return 'Follow-up';
              if (isLowRevenue && isLowBudget) return 'Lead';
              return 'Follow-up';
            })() }] : []),
            { name: 'data', value: scanAnswersWithScore }
          ],
          context: {
            pageUri: window.location.href,
            pageName: "WRKT Data-Driven Growth Scan",
            ...(hutk ? { hutk } : {})
          }
        })
      }).catch(() => {});
    } catch { }

    const resultsForEmail = scanResults ? { ...scanResults, overallScore: calculateScore() } : undefined;
    apiRequest("POST", "/api/quick-scan/unlock", {
      ...(scanId ? { scanId } : { url: contactUrl }),
      email: contactEmail,
      ...(contactPhone ? { phone: contactPhone } : {}),
      ...(resultsForEmail ? { scanResults: resultsForEmail } : {}),
      lang: language,
    }).catch(err => console.error("Lead email error:", err));

    fbqTrack('Lead', { content_name: 'Growth Scan Unlock', content_category: 'Scan' });
    gaEvent('scan_email_submit', { language });
    fbqTrack('CompleteRegistration', { content_name: 'Growth Scan Result', content_category: 'Scan' });
    gaEvent('scan_complete', { language });
    setStep(7);
  };

  const renderNav = () => {
    if (step === 0 || step >= 6) return null;
    const qIndex = step - 1;
    return (
      <div className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-6 bg-[#0A1F14]/90 backdrop-blur-md border-b border-white/10">
        <img src={logoWrkt} alt="Wrkt Digital logo" width={656} height={657} className="h-6 w-auto object-contain cursor-pointer" onClick={() => setLocation(localePath('/'))} data-testid="logo-wrkt" />
        {step >= 1 && (
          <div className="flex items-center gap-3 md:gap-4">
            <div className="flex items-center gap-1.5">
              {QUESTIONS.map((_, i) => (
                <motion.div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i < qIndex ? 'bg-[#4CEAAC]' : i === qIndex ? 'bg-[#4CEAAC] ring-2 ring-[#4CEAAC]/30' : 'bg-white/10'
                  }`}
                  initial={false}
                  animate={i < qIndex ? { scale: [1, 1.3, 1] } : {}}
                  transition={{ duration: 0.3 }}
                  data-testid={`dot-step-${i + 1}`}
                />
              ))}
            </div>
            <span className="text-[10px] font-bold text-white/50 tabular-nums tracking-wider" data-testid="text-progress-percent">
              {Math.round(((qIndex + 1) / QUESTIONS.length) * 100)}%
            </span>
          </div>
        )}
        <a href="mailto:contact@wrkt.nl" className="hidden md:block text-xs font-bold text-white/50 hover:text-[#4CEAAC] uppercase tracking-widest transition-colors">
          contact@wrkt.nl
        </a>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans selection:bg-[#4CEAAC] selection:text-[#0A1F14]" role="main">
      {renderNav()}

      <style>{`
        @keyframes radar-sweep { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.4; } 50% { transform: scale(1.05); opacity: 0.15; } 100% { transform: scale(0.9); opacity: 0.4; } }
        @keyframes terminal-blink { 0%, 100% { opacity: 0.8; } 50% { opacity: 0.3; } }
        @keyframes color-pulse-shift {
          0%, 100% { background: rgba(37, 108, 67, 0.1); }
          50% { background: rgba(76, 234, 172, 0.05); }
        }
        .scan-bg-glow { animation: color-pulse-shift 20s ease-in-out infinite; will-change: transform; }
        @media (max-width: 767px), (prefers-reduced-motion: reduce) {
          .scan-bg-glow { animation: none; will-change: auto; }
        }
      `}</style>

      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-[400px] h-[400px] md:w-[800px] md:h-[800px] rounded-full blur-[80px] md:blur-[120px] pointer-events-none mix-blend-screen scan-bg-glow" style={{ background: 'rgba(37, 108, 67, 0.1)' }} />
      </div>

      <AnimatePresence mode="wait">
        {/* STEP -1: LANDING - URL INPUT */}
        {step === -1 && (
          <motion.div key="landing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-16 lg:pt-0">
            <div className="flex flex-col justify-center px-8 lg:px-16 py-12 lg:py-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#256C43]/10 border border-[#256C43]/25 rounded-full mb-8 w-fit">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4CEAAC] animate-pulse" style={{ animationDuration: '3s' }}></span>
                <span className="text-[10px] font-bold tracking-widest uppercase text-[#4CEAAC]">
                  {nl ? 'Gratis · 2 minuten · Geen account nodig' : 'Free · 2 minutes · No account needed'}
                </span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-display font-bold leading-tight mb-6">
                {nl ? (
                  <>Ontdek waar jouw<br /><span className="text-[#4CEAAC]" style={{ textShadow: '0 0 30px rgba(76,234,172,0.3)' }}>groei blijft liggen.</span></>
                ) : (
                  <>Find out where your<br /><span className="text-[#4CEAAC]" style={{ textShadow: '0 0 30px rgba(76,234,172,0.3)' }}>growth is left behind.</span></>
                )}
              </h1>

              <p className="text-white/60 mb-8 max-w-md leading-relaxed">
                {nl
                  ? 'Vul je URL in. We scannen je advertenties, tracking, socials, SEO, performance en CRM op zeven groeifactoren. Je ontvangt een persoonlijke score met concrete verbeterpunten.'
                  : "Enter your URL. We scan your ads, tracking, socials, SEO, performance and CRM across seven growth factors. You receive a personal score with concrete improvements."}
              </p>

              <div className="space-y-4 max-w-md mb-6">
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    value={contactUrl}
                    onChange={e => { setContactUrl(e.target.value); setUrlError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') startScan(); }}
                    data-testid="input-scan-url"
                    placeholder={nl ? 'jouwwebsite.nl' : 'yourwebsite.com'}
                    className={`bg-[#141C14] border-white/10 h-16 pl-12 pr-4 text-lg text-white placeholder:text-white/30 focus-visible:ring-[#4CEAAC] rounded-xl ${urlError ? 'border-red-500/60' : ''}`}
                  />
                </div>
                {urlError && <p className="text-red-400 text-xs -mt-2" data-testid="text-url-error">{urlError}</p>}

                <Button onClick={startScan} disabled={contactUrl.length < 4} data-testid="button-start-scan"
                  className="btn btn--gradient btn--lg w-full uppercase tracking-widest">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-white mr-3">
                    <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[6px] border-l-white border-b-[4px] border-b-transparent ml-0.5"></div>
                  </span>
                  {nl ? 'Start mijn Growth Scan' : 'Start my Growth Scan'}
                </Button>
              </div>

              <p className="text-[11px] text-white/50 max-w-md">
                {nl ? 'Geen account, geen verplichtingen. Direct resultaat per mail.' : 'No account, no strings attached. Instant results by email.'}
              </p>
            </div>

            <div className="relative hidden lg:block overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F14] via-[#0A1F14]/50 to-transparent z-10"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14] via-transparent to-[#0A1F14]/60 z-10"></div>
              <div className="absolute inset-y-8 right-0 left-8 overflow-hidden rounded-l-2xl border border-white/10 opacity-60">
                <div className="signal-scroll">
                  <img src={signalReport} alt="Wrkt Growth Scan rapport voorbeeld" decoding="async" className="w-full h-auto" />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 0: SCANNING / LOADING */}
        {step === 0 && scanFailed && (
          <motion.div key="scan-failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-8">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-display mb-3">
              {nl ? 'Scan niet gelukt' : 'Scan failed'}
            </h2>
            <p className="text-white/50 text-sm mb-8 max-w-sm leading-relaxed">
              {nl
                ? `We konden ${contactUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')} niet bereiken. Dit kan komen door een DNS-fout, timeout of blokkade. Probeer het opnieuw.`
                : `We couldn't reach ${contactUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}. This could be due to a DNS error, timeout or block. Please try again.`}
            </p>
            <Button
              onClick={() => { setScanFailed(false); scanFailedRef.current = false; setStep(-1); }}
              data-testid="button-retry-scan"
              className="btn btn--gradient uppercase tracking-widest">
              {nl ? 'Opnieuw proberen' : 'Try again'}
            </Button>
          </motion.div>
        )}

        {step === 0 && !scanFailed && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="min-h-screen flex flex-col items-center justify-center p-6 text-center">

            <div className="relative w-32 h-32 md:w-36 md:h-36 mb-8">
              <div className="absolute inset-0 rounded-full border border-[#4CEAAC]/25"></div>
              <div className="absolute inset-3 rounded-full border border-[#4CEAAC]/12"></div>
              <div className="absolute inset-6 rounded-full border border-[#4CEAAC]/8"></div>
              <div className="absolute inset-x-0 top-1/2 h-px bg-[#4CEAAC]/8"></div>
              <div className="absolute inset-y-0 left-1/2 w-px bg-[#4CEAAC]/8"></div>
              <div className="absolute inset-0 rounded-full animate-[pulse-ring_5s_ease-in-out_infinite]" style={{ boxShadow: '0 0 60px rgba(76,234,172,0.18), inset 0 0 30px rgba(37,108,67,0.25)' }}></div>
              <div className="absolute inset-0 rounded-full overflow-hidden" style={{ animation: 'radar-sweep 4s linear infinite' }}>
                <div className="absolute inset-0" style={{ background: 'conic-gradient(from 0deg, rgba(76,234,172,0.28), rgba(76,234,172,0.06) 55deg, transparent 70deg)' }}></div>
                <div className="absolute top-1/2 left-1/2 w-1/2 h-[2px] origin-left bg-gradient-to-r from-[#4CEAAC] to-transparent"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-[#4CEAAC] shadow-[0_0_24px_rgba(76,234,172,0.7)] animate-pulse" style={{ animationDuration: '3s' }}></div>
              </div>
            </div>

            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#4CEAAC]">
              Wrkt Signal™ Engine
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-display mb-2">
              Scanning <span className="text-[#4CEAAC]">{contactUrl.replace(/^https?:\/\//, '').replace(/^www\./i, '').replace(/\/$/, '').toLowerCase()}</span>
            </h2>
            <p className="text-white/40 text-sm mb-8 max-w-sm">
              {nl ? 'We checken je socials, Meta Pixel, server-side tracking, conversie-events, remarketing en Ad Library.' : 'Please wait - we\'re checking your socials, Meta Pixel, server-side tracking, conversion events, remarketing and Ad Library.'}
            </p>

            <div className="w-full max-w-lg bg-gradient-to-b from-[#0D1A12] to-[#0A1F14] border border-[#4CEAAC]/15 rounded-2xl p-5 text-left font-mono shadow-[0_0_60px_rgba(37,108,67,0.25)]">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/5">
                <div className="w-2 h-2 rounded-full bg-[#4CEAAC] animate-pulse" style={{ animationDuration: '3s' }}></div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest">Live Scan</span>
                <span className="ml-auto text-[10px] text-white/40 tabular-nums">{Math.min(loadingStep, scanTaskSteps.length)}/{scanTaskSteps.length}</span>
              </div>

              <div className="space-y-2 max-h-[280px] overflow-hidden relative">
                {scanTaskSteps.map((task, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: i <= loadingStep ? 1 : 0.15, x: i <= loadingStep ? 0 : -10 }}
                    transition={{ duration: 0.3 }}
                    className={`flex items-center gap-2.5 text-xs transition-colors duration-300 ${
                      i < loadingStep ? 'text-[#4CEAAC]/60' : i === loadingStep ? 'text-[#4CEAAC]' : 'text-white/20'
                    }`}>
                    {i < loadingStep ? (
                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                    ) : i === loadingStep ? (
                      <div className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#4CEAAC]" />
                      </div>
                    ) : (
                      <div className="w-3.5 h-3.5 flex-shrink-0 rounded-full border border-white/10"></div>
                    )}
                    <span className="flex-1">
                      <span className="font-semibold">{task.label}</span>
                      {i === loadingStep && <span className="text-white/40 ml-2">- {task.detail}</span>}
                    </span>
                    {i === loadingStep && <span className="animate-[terminal-blink_2s_infinite] text-[#4CEAAC]">▌</span>}
                  </motion.div>
                ))}
                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0D1A12] to-transparent pointer-events-none"></div>
              </div>

              <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-gradient-to-r from-[#256C43] to-[#4CEAAC] rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${(Math.min(loadingStep, scanTaskSteps.length) / scanTaskSteps.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }} />
                </div>
                <span className="text-[10px] font-bold text-[#4CEAAC] tabular-nums">{Math.round((Math.min(loadingStep, scanTaskSteps.length) / scanTaskSteps.length) * 100)}%</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEPS 1-5: QUIZ QUESTIONS */}
        {step >= 1 && step <= 5 && (() => {
          const qIndex = step - 1;
          const q = QUESTIONS[qIndex];
          return (
            <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="min-h-screen pt-20 sm:pt-24 pb-16 px-6 flex items-start sm:items-center justify-center will-change-transform">
              <div className="w-full max-w-2xl">

                {step === 1 && scanResults && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
                    className="bg-[#256C43]/10 border border-[#256C43]/30 rounded-xl p-4 mb-6 sm:mb-8 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#256C43]/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-[#4CEAAC]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#4CEAAC]">
                        {nl ? 'Scan compleet!' : 'Scan complete!'}
                      </p>
                      <p className="text-xs text-white/50">
                        {nl
                          ? `${contactUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')} is geanalyseerd. Beantwoord nog ${QUESTIONS.length} aanvullende vragen zodat we een beter beeld krijgen van de kansen die er liggen.`
                          : `${contactUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')} has been analyzed. Answer ${QUESTIONS.length} more questions so we can get a better picture of the opportunities available.`}
                      </p>
                    </div>
                  </motion.div>
                )}

                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC] mb-3">
                  <span className="w-4 h-px bg-[#4CEAAC]"></span>
                  {q.cat}
                </div>

                <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 leading-tight">{q.title}</h2>
                <p className="text-white/60 text-sm mb-6 sm:mb-8 leading-relaxed">{q.sub}</p>

                {q.type === 'multi' && (
                  <p className="text-xs font-medium text-white/40 mb-4">{t('scan_q3_multi_hint')}</p>
                )}

                <div className="flex flex-col gap-3 mb-8">
                  {q.opts.map((o) => {
                    const sel = isSelected(q.id, o.v);
                    const isMulti = q.type === 'multi';
                    return (
                      <button key={o.v} onClick={() => handleOptionSelect(q.id, o.v, isMulti)}
                        data-testid={`option-${q.id}-${o.v}`}
                        className={`flex items-center text-left gap-4 p-4 rounded-xl border-2 transition-colors duration-150 ${
                          sel ? 'bg-[#256C43]/10 border-[#4CEAAC]' : 'bg-[#141C14] border-white/5 hover:border-white/20'
                        }`}>
                        <span className="w-8 flex items-center justify-center flex-shrink-0">
                          <o.i className={`w-5 h-5 ${sel ? 'text-[#4CEAAC]' : 'text-white/40'} transition-colors`} />
                        </span>
                        <span className="flex-1">
                          <span className="block font-semibold text-sm mb-0.5 text-white">{o.l}</span>
                          <span className="block text-xs text-white/50 leading-relaxed">{o.d}</span>
                        </span>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${sel ? 'bg-[#4CEAAC] scale-100 opacity-100' : 'bg-transparent border border-white/20 scale-50 opacity-0'}`}>
                          {sel && <Check className="w-3 h-3 text-[#0A1F14] font-bold" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
                  {step > 1 && (
                    <Button onClick={prevQuizStep} variant="outline" className="w-full sm:w-auto border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-full px-6 py-5 sm:py-6 h-auto text-xs font-bold uppercase tracking-widest">
                      <ArrowLeft className="w-4 h-4 mr-2" /> {t('scan_back')}
                    </Button>
                  )}
                  <Button onClick={nextQuizStep} disabled={!canGoNextQuiz()} data-testid="button-next-step"
                    className={`w-full sm:flex-1 rounded-full py-5 sm:py-6 h-auto text-xs font-bold uppercase tracking-wider sm:tracking-widest disabled:opacity-30 ${
                      qIndex === QUESTIONS.length - 1
                        ? 'bg-gradient-to-r from-[#256C43] to-[#4CEAAC] hover:from-[#1a4a2e] hover:to-[#3dbd8c] text-white shadow-[0_0_30px_rgba(76,234,172,0.3)] disabled:from-[#256C43] disabled:to-[#4CEAAC]'
                        : 'bg-[#4CEAAC] hover:bg-[#3dbd8c] text-[#0A1F14] disabled:bg-[#4CEAAC]'
                    }`}>
                    {qIndex === QUESTIONS.length - 1 ? (
                      <><Trophy className="w-4 h-4 mr-2" />{nl ? 'Bekijk mijn resultaat' : 'View my result'}</>
                    ) : (
                      <>{t('scan_next')} <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* STEP 6: EMAIL GATE - BIG EMAIL CAPTURE */}
        {step === 6 && (
          <motion.div key="email-gate" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="min-h-screen flex items-center justify-center p-6">
            <div className="w-full max-w-xl">

              <div className="bg-[#141C14] border border-white/10 rounded-3xl p-6 sm:p-8 md:p-12 text-center relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#256C43] to-[#4CEAAC]"></div>
                <div className="absolute top-0 right-1/2 translate-x-1/2 w-64 h-64 bg-[#4CEAAC]/5 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="relative z-10">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#4CEAAC]/10 border border-[#4CEAAC]/20 rounded-full mb-4"
                    data-testid="badge-check-complete"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#4CEAAC]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC]">
                      {nl ? 'Check voltooid' : 'Check complete'}
                    </span>
                  </motion.div>

                  <div className="flex justify-center mt-2">
                    <ScoreRing score={calculateScore()} size={120} />
                  </div>

                  <h2 className="text-2xl md:text-3xl font-display font-bold mt-4 mb-3">
                    {nl ? 'Je analyse is voltooid.' : 'Your analysis is complete.'}
                  </h2>
                  <p className="text-white/60 text-sm leading-relaxed mb-2 max-w-sm mx-auto">
                    {nl
                      ? 'Vul je e-mailadres in en ontvang het volledige Growth Scan-rapport, met alle bevindingen en concrete verbeterpunten.'
                      : 'Enter your email address and receive the full Growth Scan report, with all findings and concrete improvements.'}
                  </p>

                  {scanResults && (
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-1 sm:gap-2 my-6 text-xs">
                      <span className="text-white/50">{countChecks(scanResults)} {nl ? 'checks uitgevoerd' : 'checks performed'}</span>
                      <span className="text-white/15 hidden sm:inline">·</span>
                      <span className="text-[#4CEAAC] font-semibold">{countImprovements(scanResults)} {nl ? 'verbeterpunten gevonden' : 'improvements found'}</span>
                    </div>
                  )}

                  <div className="space-y-3 max-w-sm mx-auto mt-6">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <Input
                        type="email"
                        value={contactEmail}
                        onChange={e => setContactEmail(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submitEmailAndUnlock(); }}
                        data-testid="input-scan-email"
                        placeholder={nl ? 'jouw@email.nl *' : 'your@email.com *'}
                        className="bg-[#0A1F14] border-white/10 h-14 pl-12 pr-4 text-base text-white placeholder:text-white/30 focus-visible:ring-[#4CEAAC] rounded-xl"
                        autoFocus
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <Input
                        type="tel"
                        value={contactPhone}
                        onChange={e => setContactPhone(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submitEmailAndUnlock(); }}
                        data-testid="input-scan-phone"
                        placeholder={nl ? 'Telefoonnummer (optioneel)' : 'Phone number (optional)'}
                        className="bg-[#0A1F14] border-white/10 h-14 pl-12 pr-4 text-base text-white placeholder:text-white/30 focus-visible:ring-[#4CEAAC] rounded-xl"
                      />
                    </div>

                    <Button onClick={submitEmailAndUnlock} disabled={!contactEmail.includes('@')} data-testid="button-unlock-results"
                      className="btn btn--mint btn--lg w-full uppercase tracking-widest">
                      {nl ? 'Ontvang mijn rapport' : 'Get my report'} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>

                  <p className="text-[10px] text-white/25 mt-4">
                    {nl ? 'Geen spam. Je ontvangt alleen je scan rapport.' : 'No spam. You\'ll only receive your scan report.'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 7: FULL RESULTS */}
        {step === 7 && (
          <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: "easeOut" }}
            className="min-h-screen py-24 px-6 flex justify-center">
            <div className="w-full max-w-3xl">

              <div className="bg-[#141C14] border border-white/10 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden mb-6 shadow-2xl">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#256C43] to-[#4CEAAC]"></div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC] mb-6">{t('scan_score_label')}</div>
                <ScoreRing score={calculateScore()} />

                <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 mt-4">
                  {calculateScore() < 40 ? t('scan_score_low') :
                   calculateScore() < 70 ? t('scan_score_mid') :
                   t('scan_score_high')}
                </h2>

                <p className="text-sm text-white/60 leading-relaxed max-w-md mx-auto">
                  {nl
                    ? <>Je volledige rapport is onderweg naar <strong>{contactEmail}</strong>, inclusief een uitnodiging voor een Growth Review van 30 minuten. Hieronder de belangrijkste bevindingen.</>
                    : <>Your full report is on its way to <strong>{contactEmail}</strong>, including an invitation for a 30-minute Growth Review. Below are the key findings.</>}
                </p>
              </div>

              {!scanResults && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.3, ease: "easeOut" }}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6 text-center">
                  <p className="text-white/60 text-sm">
                    {nl ? 'De technische website scan kon niet automatisch worden voltooid. Je groei-score is gebaseerd op je antwoorden. We nemen contact op om een volledige analyse te bespreken.' : 'The technical website scan could not be completed automatically. Your growth score is based on your answers. We\'ll reach out to discuss a full analysis.'}
                  </p>
                  <Link href={localePath("/contact")}>
                    <Button className="btn btn--mint mt-4 uppercase tracking-widest">
                      {nl ? 'Plan je gratis Growth Review' : 'Book your free Growth Review'}
                    </Button>
                  </Link>
                </motion.div>
              )}

              {scanResults && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                          <Eye className="w-5 h-5 text-[#4CEAAC]" />
                        </div>
                        <h3 className="font-display font-bold text-sm">SEO & Meta Tags</h3>
                      </div>
                      <span className={`font-display font-bold text-lg ${scanResults.seo.score >= 70 ? 'text-[#4CEAAC]' : scanResults.seo.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {scanResults.seo.score}/100
                      </span>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.seo.title} /><span className="text-white/70">Page Title{scanResults.seo.title ? `: "${scanResults.seo.title.slice(0, 40)}${scanResults.seo.title.length > 40 ? '...' : ''}"` : ''}</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.seo.description} /><span className="text-white/70">Meta Description</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.seo.hasOgTags} /><span className="text-white/70">Open Graph Tags</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.seo.hasTwitterCards} /><span className="text-white/70">Twitter Cards</span></div>
                    </div>
                  </div>

                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                          <Share2 className="w-5 h-5 text-[#4CEAAC]" />
                        </div>
                        <h3 className="font-display font-bold text-sm">{nl ? 'Socials & Kanalen' : 'Socials & Channels'}</h3>
                      </div>
                      <span className={`font-display font-bold text-lg ${scanResults.socials.score >= 70 ? 'text-[#4CEAAC]' : scanResults.socials.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {scanResults.socials.score}/100
                      </span>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      {(['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'YouTube', 'Pinterest'] as const).map((name) => {
                        const key = name.toLowerCase() as keyof typeof scanResults.socials;
                        return (
                          <div key={name} className="flex items-center gap-2">
                            <StatusIcon active={!!scanResults.socials[key]} />
                            <span className="text-white/70">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-[#4CEAAC]" />
                        </div>
                        <h3 className="font-display font-bold text-sm">Tracking & Pixels</h3>
                      </div>
                      <span className={`font-display font-bold text-lg ${scanResults.tracking.score >= 70 ? 'text-[#4CEAAC]' : scanResults.tracking.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {scanResults.tracking.score}/100
                      </span>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.tracking.metaPixel} /><span className="text-white/70">Meta Pixel (Facebook)</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.tracking.googleAnalytics} /><span className="text-white/70">Google Analytics</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.tracking.tiktokPixel} /><span className="text-white/70">TikTok Pixel</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.tracking.googleTagManager} /><span className="text-white/70">Google Tag Manager</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.tracking.emailMarketing} /><span className="text-white/70">{nl ? 'E-mailmarketing' : 'Email marketing'}{scanResults.tracking.emailPlatform ? ` (${scanResults.tracking.emailPlatform})` : ''}</span></div>
                    </div>
                  </div>

                  {scanResults.performance && (
                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-[#4CEAAC]" />
                          </div>
                          <h3 className="font-display font-bold text-sm">Performance</h3>
                        </div>
                        <span className={`font-display font-bold text-lg ${scanResults.performance.score >= 70 ? 'text-[#4CEAAC]' : scanResults.performance.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {scanResults.performance.score}/100
                        </span>
                      </div>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.ttfbMs > 0 && scanResults.performance.ttfbMs < 800} /><span className="text-white/70">{nl ? 'Responstijd server' : 'Server response time'} ({scanResults.performance.ttfbMs} ms)</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.htmlKB < 400} /><span className="text-white/70">{nl ? 'Paginagewicht' : 'Page weight'} ({scanResults.performance.htmlKB} KB)</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.syncHeadScripts <= 3} /><span className="text-white/70">{nl ? 'Render-blocking scripts' : 'Render-blocking scripts'} ({scanResults.performance.syncHeadScripts})</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.hasLazyLoading} /><span className="text-white/70">Lazy loading</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.hasModernImages} /><span className="text-white/70">{nl ? 'Moderne afbeeldingen (WebP/AVIF)' : 'Modern image formats (WebP/AVIF)'}</span></div>
                        {scanResults.performance.crux?.lcpMs != null && (
                          <div className="flex items-center gap-2"><StatusIcon active={scanResults.performance.crux.lcpMs <= 2500} /><span className="text-white/70">LCP {nl ? 'echte bezoekers' : 'real users'} ({(scanResults.performance.crux.lcpMs / 1000).toFixed(1)}s)</span></div>
                        )}
                      </div>
                    </div>
                  )}

                  {scanResults.crm && (
                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-[#4CEAAC]" />
                          </div>
                          <h3 className="font-display font-bold text-sm">CRM & Retention</h3>
                        </div>
                        <span className={`font-display font-bold text-lg ${scanResults.crm.score >= 70 ? 'text-[#4CEAAC]' : scanResults.crm.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {scanResults.crm.score}/100
                        </span>
                      </div>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.crm.emailPlatform} /><span className="text-white/70">{nl ? 'E-mailplatform' : 'Email platform'}{scanResults.crm.emailPlatform ? ` (${scanResults.crm.emailPlatform})` : ''}</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.crm.hasSignupForm} /><span className="text-white/70">{nl ? 'Nieuwsbrief-inschrijving' : 'Newsletter signup'}</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={scanResults.crm.hasOnsiteForms} /><span className="text-white/70">{nl ? 'Popup/onsite formulieren' : 'Popup/onsite forms'}</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.crm.reviewsPlatform} /><span className="text-white/70">Reviews{scanResults.crm.reviewsPlatform ? ` (${scanResults.crm.reviewsPlatform})` : ''}</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.crm.loyaltyPlatform} /><span className="text-white/70">{nl ? 'Loyalty-programma' : 'Loyalty program'}{scanResults.crm.loyaltyPlatform ? ` (${scanResults.crm.loyaltyPlatform})` : ''}</span></div>
                        <div className="flex items-center gap-2"><StatusIcon active={!!scanResults.crm.smsPlatform} /><span className="text-white/70">{nl ? 'SMS-marketing' : 'SMS marketing'}{scanResults.crm.smsPlatform ? ` (${scanResults.crm.smsPlatform})` : ''}</span></div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-[#4CEAAC]" />
                        </div>
                        <h3 className="font-display font-bold text-sm">{nl ? 'Technisch' : 'Technical'}</h3>
                      </div>
                      <span className={`font-display font-bold text-lg ${scanResults.technical.score >= 70 ? 'text-[#4CEAAC]' : scanResults.technical.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {scanResults.technical.score}/100
                      </span>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.technical.hasHttps} /><span className="text-white/70">HTTPS / SSL</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.technical.hasViewport} /><span className="text-white/70">Mobile Viewport</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.technical.hasFavicon} /><span className="text-white/70">Favicon</span></div>
                    </div>
                  </div>

                  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 md:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#256C43]/30 to-[#4CEAAC]/10 flex items-center justify-center">
                          <Megaphone className="w-5 h-5 text-[#4CEAAC]" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-sm">{nl ? 'Advertising & Creatives' : 'Advertising & Creatives'}</h3>
                          <p className="text-[10px] text-white/40">{nl ? 'Conversie tracking & Ad Library' : 'Conversion tracking & Ad Library'}</p>
                        </div>
                      </div>
                      <span className={`font-display font-bold text-lg ${scanResults.advertising.score >= 70 ? 'text-[#4CEAAC]' : scanResults.advertising.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {scanResults.advertising.score}/100
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.advertising.hasMetaAds} /><span className="text-white/70">Meta Pixel {scanResults.advertising.metaPixelId ? <span className="text-white/30 text-[10px] ml-1">ID: {scanResults.advertising.metaPixelId}</span> : ''}</span></div>
                      {metaAds && metaAds.status !== 'none' && (
                        <div className="flex items-center gap-2">
                          {metaAds.status === 'pending'
                            ? <Loader2 className="w-4 h-4 animate-spin text-[#4CEAAC]" />
                            : <StatusIcon active={metaAds.status === 'done' && (metaAds.adsCount || 0) > 0} />}
                          <span className="text-white/70">
                            {metaAds.status === 'pending'
                              ? (nl ? 'Ad Library wordt gecheckt…' : 'Checking Ad Library…')
                              : metaAds.status === 'done'
                                ? (nl ? `Actieve Meta-ads (${metaAds.adsCount}${metaAds.capped ? '+' : ''})` : `Active Meta ads (${metaAds.adsCount}${metaAds.capped ? '+' : ''})`)
                                : (nl ? 'Ad Library-check niet gelukt' : 'Ad Library check failed')}
                          </span>
                        </div>
                      )}

                      {metaAds?.status === 'done' && (metaAds.sampleAds?.length || 0) > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/5">
                          <p className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3">{nl ? 'Live in de Ad Library' : 'Live in the Ad Library'}</p>
                          <div className="grid grid-cols-3 gap-2">
                            {metaAds.sampleAds!.map((ad, i) => (
                              <div key={i} className="rounded-lg overflow-hidden border border-white/10 bg-white/[0.02]">
                                {ad.videoUrl ? (
                                  <video src={ad.videoUrl} poster={ad.thumbnailUrl || undefined} autoPlay muted loop playsInline className="w-full aspect-square object-cover" />
                                ) : ad.thumbnailUrl ? (
                                  <img src={ad.thumbnailUrl} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                                ) : (
                                  <div className="w-full aspect-square flex items-center justify-center text-white/20 text-xs p-2 text-center">{ad.bodyText.slice(0, 60)}</div>
                                )}
                                <div className="p-2">
                                  {ad.startDate && <p className="text-[10px] text-white/40">{nl ? 'Actief sinds' : 'Active since'} {ad.startDate}</p>}
                                  {ad.bodyText && ad.thumbnailUrl && <p className="text-[10px] text-white/60 line-clamp-2 mt-1">{ad.bodyText}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                          {metaAds.aiInsight && (
                            <div className="mt-3 rounded-lg border border-[#4CEAAC]/20 bg-[#4CEAAC]/5 p-3">
                              <p className="text-[10px] font-semibold tracking-widest uppercase text-[#4CEAAC] mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3" />{nl ? 'Wrkt Signal analyse' : 'Wrkt Signal analysis'}</p>
                              <p className="text-xs text-white/75 leading-relaxed">{metaAds.aiInsight}</p>
                            </div>
                          )}
                          <p className="text-xs text-white/50 mt-3">
                            {nl
                              ? 'Dit is een fractie van wat Wrkt Signal ziet: creative-analyses, concurrenten en trends. In je Growth Strategy Call laten we het volledige beeld zien.'
                              : 'This is a fraction of what Wrkt Signal sees: creative analyses, competitors and trends. In your Growth Strategy Call we show the full picture.'}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.advertising.hasGoogleAds} /><span className="text-white/70">Google Ads {scanResults.advertising.googleAdsId ? <span className="text-white/30 text-[10px] ml-1">{scanResults.advertising.googleAdsId}</span> : ''}</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.advertising.hasConversionEvents} /><span className="text-white/70">{nl ? 'Meta Conversion Events' : 'Meta Conversion Events'}</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={(scanResults.advertising.googleConversionEvents?.length || 0) > 0} /><span className="text-white/70">{nl ? 'Google Conversion Events' : 'Google Conversion Events'}</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.advertising.hasServerSideTracking} /><span className="text-white/70">Server-Side Tracking</span></div>
                      <div className="flex items-center gap-2"><StatusIcon active={scanResults.advertising.hasDynamicRemarketing} /><span className="text-white/70">Dynamic Remarketing</span></div>
                    </div>
                    {scanResults.advertising.conversionEvents.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-2">{nl ? 'Meta events' : 'Meta events'}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {scanResults.advertising.conversionEvents.map((ev, i) => (
                            <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-[#256C43]/15 border border-[#256C43]/25 text-[#4CEAAC] font-medium">{ev}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(scanResults.advertising.googleConversionEvents?.length || 0) > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-2">{nl ? 'Google events' : 'Google events'}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {scanResults.advertising.googleConversionEvents.map((ev, i) => (
                            <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-[#1a3a5c]/30 border border-[#4285F4]/25 text-[#8AB4F8] font-medium">{ev}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {scanResults && (() => {
                const facts = nl ? [
                  !scanResults.advertising.hasGoogleAds && { fact: 'Google Shopping en Performance Max campagnes leveren gemiddeld 25% hogere ROAS dan standaard search campaigns. Voor D2C e-commerce is Google Ads een essentieel kanaal naast Meta.', source: 'Google Ads Best Practices' },
                  !scanResults.advertising.hasServerSideTracking && { fact: 'Door iOS 14.5+ en ad blockers verlies je zonder server-side tracking tot 30% van je conversiedata. Merken die CAPI implementeren zien gemiddeld 15% lagere CPA.', source: 'Meta Business Help Center' },
                  !scanResults.tracking.metaPixel && { fact: 'Bedrijven die hun Meta Pixel correct instellen behalen gemiddeld 2,5x hogere ROAS dan bedrijven zonder pixel-optimalisatie.', source: 'Meta for Business' },
                  scanResults.advertising.conversionEvents.length < 3 && { fact: 'Meta\'s algoritme heeft minimaal 50 conversie-events per week nodig om effectief te optimaliseren. Hoe meer events je deelt, hoe slimmer het algoritme wordt.', source: 'Meta Performance Marketing' },
                  scanResults.socials.score < 60 && { fact: 'D2C merken met een actieve aanwezigheid op 3+ social kanalen groeien gemiddeld 40% sneller dan merken op slechts 1 platform.', source: 'Shopify Commerce Trends' },
                  { fact: '73% van de consumenten koopt liever van merken die een persoonlijke ervaring bieden. Dynamische remarketing verhoogt je conversieratio met gemiddeld 30%.', source: 'McKinsey & Company' },
                ].filter(Boolean) : [
                  !scanResults.advertising.hasGoogleAds && { fact: 'Google Shopping and Performance Max campaigns deliver 25% higher ROAS on average compared to standard search campaigns. For D2C e-commerce, Google Ads is an essential channel alongside Meta.', source: 'Google Ads Best Practices' },
                  !scanResults.advertising.hasServerSideTracking && { fact: 'Due to iOS 14.5+ and ad blockers, you lose up to 30% of conversion data without server-side tracking. Brands implementing CAPI see an average 15% lower CPA.', source: 'Meta Business Help Center' },
                  !scanResults.tracking.metaPixel && { fact: 'Companies with properly configured Meta Pixel achieve 2.5x higher ROAS on average compared to those without pixel optimization.', source: 'Meta for Business' },
                  scanResults.advertising.conversionEvents.length < 3 && { fact: 'Meta\'s algorithm needs at least 50 conversion events per week to optimize effectively. The more events you share, the smarter the algorithm becomes.', source: 'Meta Performance Marketing' },
                  scanResults.socials.score < 60 && { fact: 'D2C brands active on 3+ social channels grow 40% faster on average than those on just 1 platform.', source: 'Shopify Commerce Trends' },
                  { fact: '73% of consumers prefer buying from brands that offer personalized experiences. Dynamic remarketing increases conversion rates by an average of 30%.', source: 'McKinsey & Company' },
                ].filter(Boolean);
                const fact = facts[0] as { fact: string; source: string };
                if (!fact) return null;
                return (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.3, ease: "easeOut" }}
                    className="bg-[#141C14] border border-[#4CEAAC]/20 rounded-2xl p-6 mb-6 relative overflow-hidden"
                    data-testid="did-you-know">
                    <div className="flex gap-4 items-start">
                      <div className="w-10 h-10 rounded-xl bg-[#4CEAAC]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-5 h-5 text-[#4CEAAC]" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC] mb-2">{nl ? 'Wist je dat?' : 'Did you know?'}</p>
                        <p className="text-sm text-white/70 leading-relaxed">{fact.fact}</p>
                        <p className="text-[10px] text-white/30 mt-2">{nl ? 'Bron' : 'Source'}: {fact.source}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })()}

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.3, ease: "easeOut" }}
                className="bg-gradient-to-br from-[#256C43]/20 via-[#141C14] to-[#4CEAAC]/10 border border-[#256C43]/30 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#256C43] to-[#4CEAAC]"></div>
                <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">{t('scan_cta_title')}</h3>
                <p className="text-sm text-white/70 mb-6 max-w-md mx-auto leading-relaxed">
                  {nl
                    ? 'Ons team gaat voorafgaand aan de call aan de slag met een diepere analyse van je Ad Library, funnel en concurrenten. In 30 minuten krijg je:'
                    : 'Ahead of the call, our team runs a deeper analysis of your Ad Library, funnel and competitors. In 30 minutes you get:'}
                </p>
                <ul className="text-left max-w-md mx-auto space-y-2.5 mb-6">
                  {(nl ? [
                    'Een uitgebreide analyse van je advertenties, creatives en campagnestructuur',
                    'Concrete kansen die we in je funnel en tracking hebben gevonden',
                    'Een strategische richting op maat, gebaseerd op wat werkt in jouw markt',
                  ] : [
                    'An in-depth analysis of your ads, creatives and campaign structure',
                    'Concrete opportunities we found in your funnel and tracking',
                    'A tailored strategic direction, based on what works in your market',
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/80">
                      <Check className="w-4 h-4 text-[#4CEAAC] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-white/50 italic mb-8 max-w-md mx-auto">
                  {nl
                    ? 'Geen verkooppraatje - een inhoudelijk gesprek. Als er een klik is, werken we een voorstel uit.'
                    : 'No sales pitch - a substantive conversation. If there\'s a fit, we work out a proposal.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => window.open('https://meetings.hubspot.com/bas41', '_blank')}
                    data-testid="button-plan-call"
                    className="btn btn--gradient uppercase tracking-widest">
                    {t('scan_cta_btn')}
                  </Button>
                </div>
                <p className="text-[11px] text-white/40 mt-3">{nl ? '30 minuten - vrijblijvend - met concrete groei-inzichten' : '30 minutes - no obligation - with concrete growth insights'}</p>
                <button onClick={() => setLocation(localePath('/'))} className="block w-full mt-4 text-[10px] font-bold text-white/40 uppercase tracking-widest hover:text-white/80 transition-colors">
                  {t('scan_back_home')}
                </button>
              </motion.div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
